import { useCallback, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { PeopleAccessHeaderContext } from '../context/PeopleAccessHeaderContext.js';
import { usePeopleAccessTabs } from '../hooks/usePeopleAccessTabs.js';
import { usePermissions } from '../hooks/usePermission.js';
import PeopleAccessHeader from './PeopleAccessHeader';

/**
 * Full-width content with the prototype's inset (8px 32px 64px) — `PageContainer`'s
 * centered `max-w-7xl` does not match it, so the wrapper lives here.
 *
 * Header + Members / Roles / Groups tabs shared by the three list screens. A list page that pins
 * its own header (the Members list) takes it over with `useOwnPeopleAccessHeader`. Full-page
 * screens (detail, invite, import, role builder) render outside it.
 *
 * Permissions (CT-5569): nothing renders until they load (no flash of the section), a tab the
 * viewer may not open is hidden (kept locked in `lock` mode), and a viewer with no visible tab
 * gets only the outlet, so the route guard redirects them or shows the no-access state.
 *
 * @param {object} props
 * @param {string} props.basePath Where the host mounted the app (see `getBasePath`).
 */
function PeopleAccessLayout({ basePath }) {
  const { loading } = usePermissions();
  const tabs = usePeopleAccessTabs();
  const [ownedBy, setOwnedBy] = useState(0);

  // Counting claims keeps a page swap (one unmounts as the next mounts) from flashing the header.
  const claimHeader = useCallback(() => {
    setOwnedBy((n) => n + 1);
    return () => setOwnedBy((n) => n - 1);
  }, []);
  const context = useMemo(() => ({ basePath, claimHeader }), [basePath, claimHeader]);

  if (loading) return null;
  if (tabs.length === 0) return <Outlet />;

  return (
    <PeopleAccessHeaderContext.Provider value={context}>
      {/* Scroll anchoring off: when a list page folds its toolbar the rows move up, and the browser
          would subtract that shift from `scrollTop` (to 0 near the end), which the sticky-header
          hook reads as "back at the top" and unfolds again. */}
      <main
        className="scrollbar-aimsos-auto-sm flex-1 overflow-y-auto bg-surface-base"
        style={{ overflowAnchor: 'none' }}
      >
        {/* The 64px bottom inset goes away while a page pins its own footer: padding below a sticky
            footer un-pins it by that much at the end of the scroll (list-page.mdc). */}
        <div
          className={['flex min-h-full flex-col gap-6 px-8 pt-2', ownedBy === 0 ? 'pb-16' : '']
            .filter(Boolean)
            .join(' ')}
        >
          {ownedBy === 0 ? <PeopleAccessHeader /> : null}
          <Outlet />
        </div>
      </main>
    </PeopleAccessHeaderContext.Provider>
  );
}

export default PeopleAccessLayout;
